import { useState } from "react";
import { BRAND } from "../types";
import type { InquiryOrder } from "../types";

export default function PaymentPage({
  order,
  onBack,
  onSuccess,
}: {
  order: InquiryOrder;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "card">("wechat");
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  const handlePay = () => {
    setPaying(true);
    setTimeout(() => {
      setPaying(false);
      setPaid(true);
      setTimeout(onSuccess, 2000);
    }, 2000);
  };

  if (paid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-8" style={{ background: "#F4F5F9" }}>
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ background: "linear-gradient(135deg, #059669, #10B981)" }}
        >
          <i className="ri-check-line text-white" style={{ fontSize: 40 }} />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">支付成功！</h2>
        <p className="text-sm text-gray-500 text-center mb-2">
          ¥{order.totalQuotedPrice?.toLocaleString()} 已支付
        </p>
        <p className="text-xs text-gray-400 text-center">正在为你生成正式订单，请稍候...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F4F5F9" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 relative overflow-hidden"
        style={{ background: "linear-gradient(145deg,#1A2238 0%,#2D3561 70%,#3D5A80 100%)" }}
      >
        <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}
            >
              <i className="ri-arrow-left-line" style={{ fontSize: 18, color: "#fff" }} />
            </button>
            <div>
              <h1 className="text-base font-bold text-white">确认支付</h1>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>安全加密支付</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3 pb-36">
        {/* 支付金额 */}
        <div
          className="rounded-2xl p-5 text-center relative overflow-hidden"
          style={{ background: "linear-gradient(145deg,#1A2238 0%,#2D3561 100%)" }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute rounded-full"
              style={{ width: 200, height: 200, top: -60, right: -40, background: "rgba(255,107,53,0.1)", filter: "blur(40px)" }}
            />
          </div>
          <div className="relative">
            <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>支付金额</p>
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="text-lg font-bold text-white">¥</span>
              <span className="text-4xl font-bold text-white">{order.totalQuotedPrice?.toLocaleString()}</span>
            </div>
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
              {order.needs.destination} · {order.needs.dates.split("（")[0]} · {order.needs.travelers}人
            </p>
          </div>
        </div>

        {/* 订单摘要 */}
        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-file-list-3-line" style={{ color: BRAND, fontSize: 14 }} />
            <span className="text-sm font-bold text-gray-800">订单摘要</span>
          </div>
          <div className="space-y-2">
            {order.products.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{p.icon}</span>
                  <span className="text-xs text-gray-600">{p.name}</span>
                </div>
                <span className="text-xs font-semibold text-gray-700">
                  ¥{(p.quotedPrice ?? p.originalPrice ?? 0).toLocaleString()}
                </span>
              </div>
            ))}
            <div className="h-px bg-gray-100 my-1" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-800">合计</span>
              <span className="text-base font-bold" style={{ color: BRAND }}>
                ¥{order.totalQuotedPrice?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* 支付方式 */}
        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-bank-card-line" style={{ color: BRAND, fontSize: 14 }} />
            <span className="text-sm font-bold text-gray-800">支付方式</span>
          </div>
          <div className="space-y-2">
            {[
              { key: "wechat" as const, icon: "ri-wechat-pay-line", label: "微信支付", color: "#07C160", desc: "推荐" },
              { key: "alipay" as const, icon: "ri-alipay-line", label: "支付宝", color: "#1677FF", desc: "" },
              { key: "card" as const, icon: "ri-bank-card-2-line", label: "银行卡", color: "#374151", desc: "" },
            ].map((method) => (
              <button
                key={method.key}
                onClick={() => setPayMethod(method.key)}
                className="w-full flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
                style={{
                  background: payMethod === method.key ? "#FFF8F0" : "#F8F9FC",
                  border: `1.5px solid ${payMethod === method.key ? BRAND : "#F0F0F0"}`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${method.color}15` }}
                >
                  <i className={method.icon} style={{ color: method.color, fontSize: 20 }} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">{method.label}</span>
                    {method.desc && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: "#FFF3EE", color: BRAND }}>
                        {method.desc}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: payMethod === method.key ? BRAND : "transparent",
                    border: `2px solid ${payMethod === method.key ? BRAND : "#D1D5DB"}`,
                  }}
                >
                  {payMethod === method.key && (
                    <i className="ri-check-line text-white" style={{ fontSize: 10 }} />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 安全提示 */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
          <i className="ri-shield-check-line" style={{ color: "#059669", fontSize: 14 }} />
          <span className="text-xs text-green-700">支付信息经过 SSL 加密保护，安全可靠</span>
        </div>
      </div>

      {/* 底部支付按钮 */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4"
        style={{ background: "rgba(255,255,255,0.97)", borderTop: "1px solid #F0F0F0", backdropFilter: "blur(20px)", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        <button
          onClick={handlePay}
          disabled={paying}
          className="w-full py-4 rounded-2xl text-base font-bold text-white cursor-pointer whitespace-nowrap active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          style={{
            background: paying ? "#E5E7EB" : "linear-gradient(135deg, #059669, #10B981)",
            color: paying ? "#9CA3AF" : "#fff",
          }}
        >
          {paying ? (
            <>
              <div
                className="w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent"
                style={{ animation: "spin 0.8s linear infinite" }}
              />
              支付中...
            </>
          ) : (
            <>
              <i className="ri-secure-payment-line" style={{ fontSize: 18 }} />
              确认支付 ¥{order.totalQuotedPrice?.toLocaleString()}
            </>
          )}
        </button>
        <p className="text-center text-xs text-gray-400 mt-2">支付即视为同意《服务协议》</p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
